/**
 * Vessel Configuration Store
 * Manages vessel configuration state with localStorage persistence
 * Stage 4A.7 - 100% Gate Coverage
 */

import { defaultVesselConfig, migrateVesselConfig } from './vesselConfig.schema';
import * as storageRouter from '../lib/storageRouter';

const STORAGE_KEY = 'sr.vesselConfig.v1';
const VERSION = '1.0.0';

class VesselConfigStore {
  constructor() {
    this.config = { ...defaultVesselConfig };
    this.listeners = new Set();
    this.initialized = false;
  }

  // Initialize store from localStorage
  init() {
    if (this.initialized) return;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        
        // Check version and migrate if needed
        if (parsed.version !== VERSION) {

          this.config = migrateVesselConfig(parsed.config || parsed);
        } else {
          this.config = parsed.config;
        }
      } else {
        // Check for legacy vessel config
        const legacyVessel = localStorage.getItem('currentVessel');
        if (legacyVessel) {

          this.config = migrateVesselConfig(JSON.parse(legacyVessel));
        }
      }
      
      this.initialized = true;
      this.save();
    } catch (error) {

      this.config = { ...defaultVesselConfig };
      this.initialized = true;
    }
  }

  // Save to localStorage
  save() {
    try {
      const data = {
        version: VERSION,
        config: this.config,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

      // PHASE 3.6 TIER 1: Parallel IndexedDB write (fire-and-forget)
      storageRouter.writeJSON(STORAGE_KEY, data).catch(error => {
        console.warn('[vesselConfig.store] Parallel IndexedDB write failed:', error);
        // localStorage write succeeded, so config update continues normally
      });
    } catch (error) {

    }
  }

  // Get current configuration
  getConfig() {
    if (!this.initialized) this.init();
    return this.config;
  }

  // Get specific section
  getSection(section) {
    if (!this.initialized) this.init();
    return this.config[section] || {};
  }

  // Get specific field value
  getField(path) {
    if (!this.initialized) this.init();
    
    const parts = path.split('.');
    let value = this.config;
    
    for (const part of parts) {
      if (value && typeof value === 'object') {
        value = value[part];
      } else {
        return undefined;
      }
    }
    
    return value;
  }

  // Update configuration
  updateConfig(updates) {
    if (!this.initialized) this.init();
    
    // Deep merge updates
    Object.entries(updates).forEach(([section, fields]) => {
      if (typeof fields === 'object' && !Array.isArray(fields)) {
        this.config[section] = {
          ...this.config[section],
          ...fields
        };
      } else {
        this.config[section] = fields;
      }
    });
    
    this.save();
    this.notifyListeners();
  }

  // Update specific section
  updateSection(section, fields) {
    if (!this.initialized) this.init();
    
    this.config[section] = {
      ...this.config[section],
      ...fields
    };
    
    this.save();
    this.notifyListeners();
  }

  // Update specific field
  updateField(path, value) {
    if (!this.initialized) this.init();
    
    const parts = path.split('.');
    const field = parts.pop();
    let target = this.config;
    
    for (const part of parts) {
      if (!target[part]) {
        target[part] = {};
      }
      target = target[part];
    }
    
    target[field] = value;
    this.save();
    this.notifyListeners();
  }

  // Reset to defaults
  reset() {
    this.config = { ...defaultVesselConfig };
    this.save();
    this.notifyListeners();
  }

  // Clear from localStorage
  clear() {
    localStorage.removeItem(STORAGE_KEY);
    this.config = { ...defaultVesselConfig };
    this.notifyListeners();
  }

  // Subscribe to changes
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Notify all listeners of changes
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.config);
      } catch (error) {

      }
    });
  }

  // Convert to vessel object for gate evaluator
  toVessel() {
    if (!this.initialized) this.init();
    
    // Flatten the nested structure for gate evaluator
    return {
      // Basic
      name: this.config.basic.name,
      type: this.config.basic.type,
      vesselType: this.config.basic.type, // Alias
      category: this.config.basic.category,
      operatingArea: this.config.basic.operatingArea,
      lengthOverall: this.config.basic.lengthOverall,
      grossTonnage: this.config.basic.grossTonnage,
      tonnage: this.config.basic.grossTonnage, // Alias
      operatingStatus: this.config.basic.operatingStatus,
      
      // Capacity
      totalPersons: this.config.capacity.pob,
      pob: this.config.capacity.pob, // Alias
      pobChildren: this.config.capacity.pobChildren,
      crewSize: this.config.capacity.crewSize,
      passengerCapacity: this.config.capacity.passengerCapacity,
      
      // Machinery
      engineType: this.config.machinery.engineType,
      engineLocation: this.config.machinery.engineLocation,
      enginePowerKW: this.config.machinery.enginePowerKW,
      enginePower: this.config.machinery.enginePowerKW, // Alias
      startMethod: this.config.machinery.startMethod,
      hasMachinerySpace: this.config.machinery.hasMachinerySpace,
      hasRudder: this.config.machinery.hasRudder,
      hasFuelSystem: this.config.machinery.hasFuelSystem,
      hasExhaustLeakRisk: this.config.machinery.hasExhaustLeakRisk,
      
      // Accommodation
      hasAccommodation: this.config.accommodation.hasAccommodation,
      hasSleeping: this.config.accommodation.hasSleeping,
      hasCooking: this.config.accommodation.hasCooking,
      hasHeating: this.config.accommodation.hasHeating,
      hasToilet: this.config.accommodation.hasToilet,
      hasOpenFlame: this.config.accommodation.hasOpenFlame,
      hasGasAppliance: this.config.accommodation.hasGasAppliance,
      hasLiquidFuelAppliance: this.config.accommodation.hasLiquidFuelAppliance,
      hasLithiumBattery: this.config.accommodation.hasLithiumBattery,
      
      // Operations
      operations: this.config.operations.operations,
      hasCrane: this.config.operations.hasCrane,
      hasLiftingDevice: this.config.operations.hasLiftingDevice,
      hasLiftingOps: this.config.operations.lifting,
      hasDangerousGoods: this.config.operations.hasDangerousGoods,
      hasRescueBoat: this.config.operations.hasRescueBoat,
      hasFastRescueBoat: this.config.operations.hasFastRescueBoat,
      hasPersonnelTransfer: this.config.operations.personnelTransfer,
      hasTowingOps: this.config.operations.towing,
      hasBunkeringOps: this.config.operations.bunkering,
      hasInternationalVoyage: this.config.operations.hasInternationalVoyage,
      hasImmersionSuits: this.config.operations.hasImmersionSuits,
      hasInflatableLifejackets: this.config.operations.hasInflatableLifejackets,
      hasOpenLiferaft: this.config.operations.hasOpenLiferaft,
      hasMultipleLiferafts: this.config.operations.hasMultipleLiferafts,
      hasLineThrowing: this.config.operations.hasLineThrowing,
      hasDiveBasket: this.config.operations.hasDiveBasket,
      hasDiveLadder: this.config.operations.hasDiveLadder,
      hasDiverLift: this.config.operations.hasDiverLift,
      hasGrabBag: this.config.operations.hasGrabBag,

      // Navigation & Communications
      seaArea: this.config.navcomms.seaArea,
      hasECDIS: this.config.navcomms.hasECDIS,
      hasPaperCharts: this.config.navcomms.hasPaperCharts,
      hasPaperPubs: this.config.navcomms.hasPaperPubs,
      hasElectronicPubs: this.config.navcomms.hasElectronicPubs,
      hasRadarReflector: this.config.navcomms.hasRadarReflector,
      hasGeneralAlarm: this.config.navcomms.hasGeneralAlarm,
      hasFixedVHF: this.config.navcomms.hasFixedVHF,
      hasPortableVHF: this.config.navcomms.hasPortableVHF,
      hasMF: this.config.navcomms.hasMF,
      hasROC: this.config.navcomms.hasROC,
      hasSART: this.config.navcomms.hasSART,

      // Environmental & Safety
      operatingWaterTempC: this.config.environmental.operatingWaterTempC,
      operatesInPDA: this.config.environmental.operatesInPDA,
      hasProhibitedDischargeArea: this.config.environmental.operatesInPDA, // Alias
      operatesInlandOnly: this.config.environmental.operatesInlandOnly,
      hasFireRiskArea: this.config.environmental.hasFireRiskArea,
      hasIMOSewageSystem: this.config.environmental.hasIMOSewageSystem,
      hasOilySludge: this.config.environmental.hasOilySludge,
      hasGasLeakRisk: this.config.environmental.hasGasLeakRisk,
      hasElectricalSystem: this.config.environmental.hasElectricalSystem,
      hasConcentratedElectrical: this.config.environmental.hasConcentratedElectrical,
      hasFixedFireSystem: this.config.environmental.hasFixedFireSystem,
      hasFirePump: this.config.environmental.hasFirePump,
      hasEscapeRoute: this.config.environmental.hasEscapeRoute,
      hasSubstantialEnclosure: this.config.environmental.hasSubstantialEnclosure,
      hasOpenSteeringPosition: this.config.environmental.hasOpenSteeringPosition,
      
      // Computed helpers for gates
      hasOutboard: this.config.machinery.engineType === 'outboard',
      hasInboardDiesel: this.config.machinery.engineType === 'inboard_diesel',
      hasElectricStart: this.config.machinery.startMethod === 'electric',
      hasAirStart: this.config.machinery.startMethod === 'air',
      hasRemoteBoarding: this.config.operations.personnelTransfer || this.config.operations.diving
    };
  }

  // Check if wizard has been completed
  isComplete() {
    if (!this.initialized) this.init();
    
    // Check if critical fields are filled
    return !!(
      this.config.basic.name &&
      this.config.basic.type &&
      this.config.basic.category &&
      this.config.basic.lengthOverall > 0 &&
      this.config.capacity.pob > 0 &&
      this.config.machinery.engineType
    );
  }

  // Get missing critical fields
  getMissingFields() {
    if (!this.initialized) this.init();
    
    const missing = [];
    
    if (!this.config.basic.name) missing.push('Vessel Name');
    if (!this.config.basic.type) missing.push('Vessel Type');
    if (!this.config.basic.category) missing.push('WBC3 Category');
    if (!this.config.basic.lengthOverall) missing.push('Length Overall');
    if (!this.config.capacity.pob) missing.push('Persons on Board');
    if (!this.config.machinery.engineType) missing.push('Engine Type');
    
    return missing;
  }
}

// Create singleton instance
const vesselConfigStore = new VesselConfigStore();

// Export store and helper functions
export default vesselConfigStore;

// React hook for using the store
export function useVesselConfigStore() {
  const [config, setConfig] = React.useState(() => vesselConfigStore.getConfig());
  
  React.useEffect(() => {
    const unsubscribe = vesselConfigStore.subscribe(setConfig);
    return unsubscribe;
  }, []);
  
  return {
    config,
    updateConfig: (updates) => vesselConfigStore.updateConfig(updates),
    updateSection: (section, fields) => vesselConfigStore.updateSection(section, fields),
    updateField: (path, value) => vesselConfigStore.updateField(path, value),
    reset: () => vesselConfigStore.reset(),
    isComplete: () => vesselConfigStore.isComplete(),
    getMissingFields: () => vesselConfigStore.getMissingFields(),
    toVessel: () => vesselConfigStore.toVessel()
  };
}